/* Disco Chores — shared motion.
   Scroll-up reveals, the marquee, and the hero: the lattice, the list that
   crosses itself off, and the mirror ball you uncover with the cursor.

   Enqueue once in theme.liquid:
   {{ 'dc-motion.js' | asset_url | script_tag }} (defer) */
(function(){
  const $ = s => document.querySelector(s);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const CELL = 28;                       // locked to the check's 56px tile in dc-brand.css

  function toast(msg){
    const el = $("#dc-toast") || $("#toast");
    if (!el) return;
    el.textContent = msg;
    el.classList.add("is-on");
    clearTimeout(toast._t);
    toast._t = setTimeout(() => el.classList.remove("is-on"), 1700);
  }

  /* ---------------- scroll-up arrival ----------------
     Re-scannable: call window.scanReveals() after injecting new
     .reveal-up elements (a filtered product grid, say). */
  document.documentElement.classList.add("js");
  const scanReveals = (function(){
    if (!("IntersectionObserver" in window)){
      return () => document.querySelectorAll(".reveal-up").forEach(el => el.classList.add("is-in"));
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting){ e.target.classList.add("is-in"); io.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.05 });
    return function(){
      document.querySelectorAll(".reveal-up:not([data-rv])").forEach(el => {
        el.setAttribute("data-rv","1");
        io.observe(el);
      });
    };
  })();
  window.scanReveals = scanReveals;
  scanReveals();

  /* ---------------- hero lattice ---------------- */
  function buildHeroBg(){
    const el = $("#heroBg");
    const N = 2000;
    const cells = [];
    for (let i = 0; i < N; i++) cells.push("<i></i>");
    el.innerHTML = cells.join("");
  }

  /* Which check square each cell sits on.
     The ground is a conic checkerboard at one square per cell, so the answer is
     the parity of (row + col) — but CSS has no idea how many columns
     repeat(auto-fill,28px) ended up fitting, so it cannot work this out. We tag
     every cell and dc-brand.css keys its blue tiers and glints off the class.
     Re-run whenever the column count can have changed. */
  function tagSquares(){
    const bg = document.getElementById("heroBg");
    if (!bg || !bg.children.length) return;
    const tracks = getComputedStyle(bg).gridTemplateColumns;
    let cols = 0;
    if (tracks && tracks !== "none") cols = tracks.trim().split(/\s+/).filter(Boolean).length;
    if (cols < 2) cols = Math.max(1, Math.floor(bg.getBoundingClientRect().width / CELL));
    const kids = bg.children;
    for (let i = 0; i < kids.length; i++){
      // The conic tile paints its top-left quadrant cream, so cell 0 sits on
      // cream and a cell is blue when (row + col) is ODD.
      const blue = ((((i / cols) | 0) + (i % cols)) % 2) === 1;
      kids[i].classList.toggle("sq-blue", blue);
      kids[i].classList.toggle("sq-cream", !blue);
    }
  }

  let ballApi = null;

  /* ---------------- the mirror ball you uncover with the cursor ---------------- */
  function heroReveal(){
    const hero = document.querySelector(".hero");
    const bg = $("#heroBg");
    const hint = $("#heroHint");
    if (!hero || !bg) return;

    const kids = Array.from(bg.children);
    let cols = 1, rows = 1, bx = 0, by = 0, rad = 7, total = 0;
    let order = [], found = 0, finished = false, touched = false, stacked = false;
    const EDGE = 2.2;                       // how deep the soft rim runs, in cells

    /* Below the layout breakpoint the hero stacks: the ball hangs from the top
       edge and the copy sits under it, because there is no second column to put
       it in. Read off .hero-side's own display rather than repeating the 900px,
       so the script and the stylesheet can never drift apart. */
    function isStacked(){
      const side = document.querySelector(".hero .hero-side");
      return !side || getComputedStyle(side).display === "none";
    }

    function cellAt(col, row){
      if (col < 0 || col >= cols || row < 0) return null;
      return kids[row * cols + col] || null;
    }
    function dist(col, row){
      const dx = col - bx, dy = row - by;
      return Math.sqrt(dx*dx + dy*dy);
    }
    function isBall(col, row){ return dist(col, row) <= rad; }

    /* The deep end of the brand sheet, and deliberately so. Whether the ball
       reads at all is decided by the LIGHTNESS gap between facet and ground,
       not by saturation. The check sits near L 88; the pale end of the palette
       (bubblegum, orchid-light, pale peri, mint) sits at L 76-86 — the same
       weight as the ground, and hue alone cannot hold an edge at 28px. Orchid
       and periwinkle sit 17-26 points below it, and that gap is the ball.
       White replaces the orange that used to sit in slot 4: on a blue ground it
       reads as the mirror catching light. */
    const FACETS = [[197,127,208],[133,163,224],[192,168,222],[255,255,255],
                    [242,184,216],[197,127,208],[133,163,224],[192,168,222],[223,170,227]];
    const LIT = [255,253,250], DARK = [30,32,51];
    const mix = (a, b, k) => a.map((v, i) => Math.round(v + (b[i] - v) * k));
    const rgb = c => "rgb(" + c[0] + "," + c[1] + "," + c[2] + ")";
    // a scattering of yellow facets — the light, used sparingly
    const YELLOW = [243,226,76];
    const isSpark = (col, row) => ((col * 5 + row * 13) % 19) === 0;

    function facetColor(col, row){
      if (isSpark(col, row)) return rgb(YELLOW);
      const dx = (col - bx) / rad, dy = (row - by) / rad;
      const l = -(dx * 0.45) - (dy * 0.85);            // +1 upper left, -1 lower right
      let c = FACETS[Math.abs(row * 3 + col * 2) % FACETS.length];
      if (l > 0) c = mix(c, LIT,  Math.min(1,  l) * 0.20);
      else       c = mix(c, DARK, Math.min(1, -l) * 0.26);
      return rgb(c);
    }

    function light(cell, col, row){
      if (cell.classList.contains("ball-a") || cell.classList.contains("ball-b")) return false;
      cell.classList.remove("lit");                     // no sparkles inside the ball
      cell.classList.add((row + col) % 2 ? "ball-a" : "ball-b");
      cell.style.background = facetColor(col, row);
      // rim cells shrink, round off and fade, so the edge feathers into the
      // grid. Softer than this and the ball scatters into dots at its edge.
      const t = Math.max(0, Math.min(1, (dist(col, row) - (rad - EDGE)) / EDGE));
      if (t > 0){
        cell.style.transform    = "scale(" + (1 - 0.50 * t).toFixed(3) + ")";
        cell.style.borderRadius = (2 + t * 46).toFixed(0) + "%";
        cell.style.opacity      = (1 - 0.32 * t).toFixed(2);
      }
      found++;
      return true;
    }
    function reset(){
      kids.forEach(k => {
        k.classList.remove("ball-a","ball-b","cord","lit","glint");
        k.style.transform = ""; k.style.borderRadius = ""; k.style.opacity = ""; k.style.background = "";
      });
      bg.classList.remove("found");
      found = 0; finished = false;
    }

    /* Count the grid's REAL columns instead of dividing the hero's width by
       CELL. .hero-bg is inset:-2px, so it is 4px wider than .hero, and
       repeat(auto-fill,28px) can fit one more column than the arithmetic
       predicts. A cols that is off by one skews kids[row*cols+col] by a cell
       per row, which turns the ball into diagonal streaks. */
    function measuredCols(){
      const tracks = getComputedStyle(bg).gridTemplateColumns;
      if (tracks && tracks !== "none"){
        const n = tracks.trim().split(/\s+/).filter(Boolean).length;
        if (n > 1) return n;
      }
      return Math.max(1, Math.floor(bg.getBoundingClientRect().width / CELL));
    }

    /* The to-do list sits centred under the ball, and the ball's position is
       cell indices, not a percentage — so the stylesheet cannot express it.
       Publish it. Measured against .hero-inner, which is position:relative and
       therefore the containing block; measuring from .hero puts the list on
       top of the ball. */
    function placeSide(){
      const inner = document.querySelector(".hero .hero-inner");
      if (!inner) return;
      const hr = bg.getBoundingClientRect(), ir = inner.getBoundingClientRect();
      inner.style.setProperty("--ball-cx",     ((bx + 0.5) * CELL - (ir.left - hr.left)).toFixed(1) + "px");
      inner.style.setProperty("--ball-bottom", ((by + rad + 1) * CELL - (ir.top  - hr.top )).toFixed(1) + "px");
    }

    function measure(){
      const r = bg.getBoundingClientRect();
      cols = measuredCols();
      rows = Math.max(1, Math.floor(r.height / CELL));
      stacked = isStacked();
      if (stacked){
        /* Roughly two thirds of the screen: 7 cells on a 320px phone, 9 through
           the common phone widths, 11 on a tablet. The radius is deliberately
           NOT a whole number. At an integer radius only one cell sits at each of
           N/S/E/W — its diagonal neighbours fall outside — and at this size those
           four read as detached dots rather than the edge of a ball. The extra
           .4 pulls in the (±rad, ±1) cells so every extreme is a run of three. */
        rad = cols <= 12 ? 3.4 : (cols >= 20 ? 5.4 : 4.4);
        // centred on the hero, to the nearest cell
        const R = Math.ceil(rad);
        bx  = Math.max(R, Math.min(cols - R - 1, Math.round(r.width / (2 * CELL) - 0.5)));
        by  = R + 2;                              // two rows of cord above it
      } else {
        rad = cols < 26 ? 5 : 7;
        // bx and the headline's size compete for the same horizontal space. At
        // .705 and a 4.1rem headline they clear each other by ~9px at 1500px.
        // Moving the ball left means the headline must come down.
        bx  = Math.min(cols - rad - 1, Math.round(cols * 0.705));
        by  = Math.max(rad + 2, Math.round(rows * 0.50));
      }
      // How much room the hero must keep clear above the copy. Setting this
      // changes the hero's height, not its column count, so it settles in one
      // pass — it cannot feed back into cols and loop.
      hero.style.setProperty("--dc-ball-space", stacked ? ((by + Math.ceil(rad) + 1) * CELL) + "px" : "");
      order = [];
      const RD = Math.ceil(rad);                  // rad can be fractional; indices cannot
      for (let d = -RD; d <= RD; d++)
        for (let e = -RD; e <= RD; e++)
          if (isBall(bx + d, by + e)) order.push([bx + d, by + e]);
      total = order.length;
      order.sort((p, q) => ((p[0]*7 + p[1]*13) % 19) - ((q[0]*7 + q[1]*13) % 19));
      reset();
      tagSquares();                               // the column count just changed
      for (let r2 = 0; r2 <= by - Math.ceil(rad) - 1; r2++){ // hangs from the top edge
        const c = cellAt(bx, r2);
        if (c) c.classList.add("cord");
      }
      if (!stacked) placeSide();                  // the list is hidden when stacked
    }

    function drawBall(){
      order.forEach(([col, row]) => { const c = cellAt(col, row); if (c) light(c, col, row); });
    }

    /* With no cursor to uncover it — reduced motion, or a touch screen — the
       ball is simply there, complete and still. */
    const interactive = !reduceMotion && window.matchMedia("(hover: hover)").matches;

    measure();
    let rt;
    window.addEventListener("resize", () => {
      clearTimeout(rt);
      rt = setTimeout(() => { measure(); if (!interactive) drawBall(); }, 200);
    });

    if (!interactive){
      drawBall();
      if (hint) hint.classList.add("gone");
      return;
    }

    function finish(){
      finished = true;
      drawBall();
      bg.classList.add("found");
      if (hint){ hint.textContent = "You found it"; hint.classList.remove("gone"); setTimeout(() => hint.classList.add("gone"), 2200); }
      toast("You found it.");
    }

    hero.addEventListener("pointermove", e => {
      if (e.pointerType === "touch") return;
      if (!touched){ touched = true; hint && hint.classList.add("gone"); }
      const r = bg.getBoundingClientRect();          // same box the cells live in
      const col = Math.floor((e.clientX - r.left) / CELL);
      const row = Math.floor((e.clientY - r.top) / CELL);
      const cell = cellAt(col, row);
      if (!cell) return;

      if (isBall(col, row)){
        light(cell, col, row);
        [[1,0],[-1,0],[0,1],[0,-1]].forEach(([dx,dy]) => {
          if (!isBall(col+dx, row+dy)) return;
          const n = cellAt(col+dx, row+dy);
          if (n) light(n, col+dx, row+dy);
        });
        if (!finished && found >= Math.round(total * 0.62)) finish();
      } else if (!cell.classList.contains("lit")){
        cell.classList.add("lit");
        setTimeout(() => cell.classList.remove("lit"), 850);
      }
    });

    ballApi = {
      seed(n){
        if (finished) return;
        let placed = 0;
        for (const [col, row] of order){
          if (placed >= n || found >= total * 0.45) break;
          const c = cellAt(col, row);
          if (c && light(c, col, row)) placed++;
        }
      }
    };
  }

  /* ---------------- the list crosses itself off ---------------- */
  function heroList(){
    const list = $("#heroList");
    if (!list) return;
    const rows = Array.from(list.children);
    if (reduceMotion){ rows.slice(0,2).forEach(r => r.classList.add("done")); return; }

    let i = 0;
    (function tick(){
      if (i < rows.length){
        rows[i].classList.add("done");
        if (ballApi) ballApi.seed(9);          // each finished item builds a little of it
        i++;
        setTimeout(tick, 1250);
      } else {
        setTimeout(() => {
          rows.forEach(r => r.classList.remove("done"));   // the list resets; the ball never does
          i = 0;
          setTimeout(tick, 900);
        }, 2600);
      }
    })();
  }

  /* ---------------- marquee ----------------
     Five items printed twice and slid by exactly one copy's width. The colour
     cycle in dc-brand.css is keyed to 5 to match; change the item count and
     the 5n selectors must change with it. */
  function buildMarquee(){
    const el = $("#dc-marquee");
    if (!el) return;
    const items = (el.dataset.items || "").split("|").map(s => s.trim()).filter(Boolean);
    if (!items.length) return;
    const html = items.map(t => "<span>"+t+"</span>").join("");
    el.innerHTML = html + html;
  }

  /* ---------------- boot ---------------- */
  let heroDone = false;
  function init(){
    const bg = $("#heroBg");
    if (bg && !heroDone){
      heroDone = true;
      buildHeroBg();
      tagSquares();
      heroReveal();      // wires the cursor, or draws the ball outright
      heroList();
    }
    buildMarquee();
    scanReveals();
  }

  // Boot now if the document is already parsed (the theme editor injects
  // sections long after DOMContentLoaded has fired), otherwise wait for it.
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  // Shopify's theme editor re-renders a section on every settings change.
  document.addEventListener("shopify:section:load", function(){
    heroDone = false;          // the editor swaps the node, so rebuild it
    init();
  });
})();
